import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { AlertCircle, Ellipsis, ListCheck, Trash } from "lucide-react"
import { useCapabilityActions } from "modules/capabilities/hooks/useCapabilityActions"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CapabilitiesMarketplaceResponseDataSchema, CapabilitiesResponseDataSchema } from "schema"
import z from "zod"
import { CapabilityExecutionsSheet } from "../CapabilityExecutionsSheet"
import { UninstallCapabilityDialog } from "../UninstallCapabilityDialog"

type CapabilityCardActionsMenuProps = {
  capability:
    | z.infer<typeof CapabilitiesResponseDataSchema>[number]
    | z.infer<typeof CapabilitiesMarketplaceResponseDataSchema>[number]
  campaignId?: string
}

export function CapabilityCardActionsMenu({
  capability,
  campaignId,
}: CapabilityCardActionsMenuProps) {
  const { t } = useTranslation()
  const { canUninstall, uninstallDisabledReason } = useCapabilityActions(capability)
  const [isExecutionsSheetOpen, setIsExecutionsSheetOpen] = useState(false)
  const [isUninstallDialogOpen, setIsUninstallDialogOpen] = useState(false)

  const uninstallDisabled = !canUninstall

  const uninstallMenuItem = useMemo(() => {
    if (campaignId) return null

    const handleUninstallClick = () => {
      if (uninstallDisabled) {
        return
      }
      setIsUninstallDialogOpen(true)
    }

    const content = (
      <DropdownMenuItem disabled={uninstallDisabled} onClick={handleUninstallClick}>
        <Trash className="size-4" />
        <span>{t("capabilities.list.uninstall.cta")}</span>
      </DropdownMenuItem>
    )

    if (uninstallDisabled) {
      return (
        <Tooltip>
          <TooltipTrigger>{content}</TooltipTrigger>
          <TooltipContent
            variant="card"
            side="bottom"
            className="flex flex-col items-start gap-0.5 p-4 shadow-md"
          >
            <div className="flex flex-row items-center gap-1">
              <AlertCircle className="size-4 shrink-0 text-muted-foreground" strokeWidth={2} />
              <p className="text-sm font-semibold text-foreground leading-normal">
                {t("capabilities.list.uninstall.disabled.title")}
              </p>
            </div>
            <div className="text-sm text-muted-foreground leading-normal whitespace-pre-line text-left">
              {uninstallDisabledReason}
            </div>
          </TooltipContent>
        </Tooltip>
      )
    }

    return content
  }, [campaignId, uninstallDisabled, t, uninstallDisabledReason])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" title={t("capabilities.list.actions")}>
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => setIsExecutionsSheetOpen(true)}>
            <ListCheck className="size-4" />
            <span>{t("capabilities.list.executions")}</span>
          </DropdownMenuItem>
          {uninstallMenuItem}
        </DropdownMenuContent>
      </DropdownMenu>
      {isExecutionsSheetOpen && "id" in capability && (
        <CapabilityExecutionsSheet
          capability={capability}
          onOpenChange={setIsExecutionsSheetOpen}
          campaignId={campaignId}
        />
      )}
      {isUninstallDialogOpen && (
        <UninstallCapabilityDialog
          capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
          onOpenChange={setIsUninstallDialogOpen}
        />
      )}
    </>
  )
}
